<template>
  <FlashNotifications ref="flash" />

  <div class="flex flex-col h-app-screen pb-14 md:pb-0 bg-gray-50 dark:bg-[#0d1117]">
    <!-- Header: back to the jobs, the title, and Save -->
    <header class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      <div class="flex items-center gap-3 px-4 sm:px-6 h-14">
        <router-link to="/jobs" data-turbo-prefetch="false" aria-label="Back to Job Listings"
          class="inline-flex items-center justify-center w-8 h-8 -ml-1.5 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <svg class="w-4.5 h-4.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>
        </router-link>
        <div class="min-w-0">
          <h1 class="text-lg font-bold tracking-tight text-gray-900 dark:text-white leading-tight">Add a job</h1>
          <p class="hidden sm:block text-xs text-gray-500 dark:text-gray-400">For a job you found anywhere else. It is saved like the ones from LinkedIn, so you can write a resume for it.</p>
        </div>
        <div class="ml-auto flex items-center gap-2">
          <router-link to="/jobs" data-turbo-prefetch="false"
            class="hidden sm:inline-flex items-center h-8 px-3 rounded-lg text-[13px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">Cancel</router-link>
          <button type="submit" form="job-form" :disabled="saving"
            class="inline-flex items-center gap-1.5 h-8 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-[13px] font-semibold text-white transition-colors disabled:opacity-60 disabled:cursor-wait focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-1">
            <svg v-if="saving" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"/></svg>
            {{ saving ? 'Saving...' : 'Save job' }}
          </button>
        </div>
      </div>
    </header>

    <div class="flex-1 min-h-0 overflow-y-auto">
      <form id="job-form" novalidate @submit.prevent="save" class="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <!-- What the server said when it could not save -->
        <div v-if="serverError" class="flex items-start gap-3 rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-900/15 px-4 py-3" role="alert">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
          <p class="text-sm text-red-800 dark:text-red-200">
            {{ serverError }}
            <button v-if="existingId" type="button" @click="openExisting" class="font-semibold underline">Open that job</button>
          </p>
        </div>

        <!-- The job: all three are needed -->
        <section class="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1C2128]" aria-labelledby="job-basics">
          <div class="px-5 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <h2 id="job-basics" class="font-semibold text-gray-900 dark:text-white">The job</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">These three are needed.</p>
          </div>
          <div class="px-5 sm:px-6 py-5 space-y-5">
            <div>
              <label for="job-title" :class="LABEL">Job title <span class="text-red-500" aria-hidden="true">*</span></label>
              <input id="job-title" v-model="form.title" type="text" maxlength="300" autocomplete="off" placeholder="Senior Ruby Engineer"
                :aria-invalid="Boolean(errors.title)" :class="[INPUT, errors.title ? INVALID : '']" />
              <p v-if="errors.title" :class="ERROR">{{ errors.title }}</p>
            </div>

            <div>
              <label for="job-url" :class="LABEL">Where you found it <span class="text-red-500" aria-hidden="true">*</span></label>
              <input id="job-url" v-model="form.url" type="url" inputmode="url" autocomplete="off" placeholder="https://company.com/careers/senior-ruby-engineer"
                :aria-invalid="Boolean(errors.url)" :class="[INPUT, errors.url ? INVALID : '']" />
              <p v-if="errors.url" :class="ERROR">{{ errors.url }}</p>
              <p v-else :class="HINT">The address of the job posting. It is also the apply link on the job.</p>
            </div>

            <div>
              <label id="job-description-label" :class="LABEL">Description <span class="text-red-500" aria-hidden="true">*</span></label>
              <div role="group" aria-labelledby="job-description-label" :class="errors.description ? 'rounded-lg ring-2 ring-red-500/40' : ''">
                <quill-editor :content="form.description" :enable="true" :opts="{ height: '340px' }" @update-content="onDescription" />
              </div>
              <p v-if="errors.description" :class="ERROR">{{ errors.description }}</p>
              <p v-else :class="HINT">Paste the whole posting. Headings, bullet lists and numbered lists are kept.</p>
            </div>
          </div>
        </section>

        <!-- Everything else is optional -->
        <section class="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1C2128]" aria-labelledby="job-details">
          <div class="px-5 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <h2 id="job-details" class="font-semibold text-gray-900 dark:text-white">Details</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">Optional. The more you add, the better the resume written for this job.</p>
          </div>
          <div class="px-5 sm:px-6 py-5 space-y-5">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label for="job-company" :class="LABEL">Company</label>
                <input id="job-company" v-model="form.company_name" type="text" maxlength="200" autocomplete="off" placeholder="Acme GmbH" :class="INPUT" />
              </div>
              <div>
                <label for="job-company-url" :class="LABEL">Company website or LinkedIn address</label>
                <input id="job-company-url" v-model="form.company_url" type="url" inputmode="url" autocomplete="off" placeholder="https://acme.com"
                  :aria-invalid="Boolean(errors.company_url)" :class="[INPUT, errors.company_url ? INVALID : '']" />
                <p v-if="errors.company_url" :class="ERROR">{{ errors.company_url }}</p>
              </div>
              <div>
                <label for="job-location" :class="LABEL">Location</label>
                <input id="job-location" v-model="form.location" type="text" maxlength="200" autocomplete="off" placeholder="Berlin, Germany" :class="INPUT" />
              </div>
              <div>
                <label for="job-posted" :class="LABEL">Date posted</label>
                <input id="job-posted" v-model="form.posted_on" type="date" :max="today" :class="INPUT" />
                <p :class="HINT">Leave it empty to use today.</p>
              </div>
            </div>

            <div v-for="group in CHOICES" :key="group.field" role="group" :aria-labelledby="`choice-${group.field}`">
              <span :id="`choice-${group.field}`" :class="LABEL">{{ group.label }}</span>
              <div class="flex flex-wrap gap-2">
                <button v-for="option in group.options" :key="option" type="button" @click="choose(group.field, option)" :aria-pressed="form[group.field] === option"
                  class="h-8 px-3 rounded-full border text-[13px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
                  :class="form[group.field] === option
                    ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'">{{ option }}</button>
              </div>
            </div>

            <div>
              <label for="job-salary" :class="LABEL">Salary</label>
              <input id="job-salary" v-model="form.salary" type="text" maxlength="100" autocomplete="off" placeholder="80k to 100k EUR a year" :class="INPUT" />
            </div>
          </div>
        </section>

        <div class="flex items-center justify-end gap-2 pb-6 sm:hidden">
          <router-link to="/jobs" data-turbo-prefetch="false" class="inline-flex items-center h-9 px-4 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300">Cancel</router-link>
          <button type="submit" :disabled="saving" class="inline-flex items-center h-9 px-5 rounded-lg bg-blue-600 text-sm font-semibold text-white disabled:opacity-60">{{ saving ? 'Saving...' : 'Save job' }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useFetchedJobsStore } from 'stores/fetchedJobs.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'

const LABEL = 'block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5'
const INPUT = 'w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0d1117] text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
const INVALID = 'border-red-400! dark:border-red-500! focus:ring-red-500/20!'
const ERROR = 'mt-1.5 text-xs text-red-600 dark:text-red-400'
const HINT = 'mt-1.5 text-xs text-gray-400 dark:text-gray-500'

// The choices on the job, as LinkedIn words them. Pressing the chosen one again clears it.
const CHOICES = [
  { field: 'workplace_type', label: 'Workplace', options: ['On-site', 'Remote', 'Hybrid'] },
  { field: 'employment_type', label: 'Employment type', options: ['Full-time', 'Part-time', 'Contract', 'Temporary', 'Internship', 'Volunteer', 'Other'] },
  { field: 'experience_level', label: 'Experience level', options: ['Internship', 'Entry level', 'Associate', 'Mid-Senior level', 'Director', 'Executive'] },
]

const router = useRouter()
const jobs = useFetchedJobsStore()
const flash = ref(null)

const form = reactive({
  title: '', url: '', description: '', company_name: '', company_url: '', location: '', posted_on: '',
  workplace_type: '', employment_type: '', experience_level: '', salary: '',
})
const errors = reactive({ title: '', url: '', description: '', company_url: '' })
const saving = ref(false)
const serverError = ref('')
const existingId = ref(null)
const today = computed(() => new Date().toISOString().slice(0, 10))

// A mark on a field goes away as soon as the person starts putting it right.
for (const field of ['title', 'url', 'company_url']) watch(() => form[field], () => { errors[field] = '' })

function choose(field, option) {
  form[field] = form[field] === option ? '' : option
}

function onDescription(html) {
  form.description = html
  if (errors.description && hasText(html)) errors.description = ''
}

// An empty editor still holds <p><br></p>, so what counts is whether there is any text in it.
function hasText(html) {
  return Boolean(new DOMParser().parseFromString(html || '', 'text/html').body.textContent.trim())
}

function isWebAddress(value) {
  try {
    return ['http:', 'https:'].includes(new URL(value).protocol)
  } catch {
    return false
  }
}

// What can be told before asking the server. The server checks again, and its words are shown when it still refuses.
function validate() {
  errors.title = form.title.trim() ? '' : 'Add the job title.'
  errors.url = !form.url.trim() ? 'Add the address of the job posting.' : isWebAddress(form.url.trim()) ? '' : 'Use a web address that starts with http:// or https://'
  errors.description = hasText(form.description) ? '' : 'Add the job description.'
  errors.company_url = !form.company_url.trim() || isWebAddress(form.company_url.trim()) ? '' : 'Use a web address that starts with http:// or https://'
  return !errors.title && !errors.url && !errors.description && !errors.company_url
}

async function save() {
  if (saving.value) return
  serverError.value = ''
  existingId.value = null
  if (!validate()) {
    flash.value?.show('Please fix what is marked.', 'alert')
    return
  }

  saving.value = true
  const result = await jobs.createJob({ ...form })
  saving.value = false

  if (!result.ok) {
    serverError.value = result.message
    existingId.value = result.existingId
    return
  }
  router.push({ path: '/jobs', query: { job: result.job.id } })
}

function openExisting() {
  router.push({ path: '/jobs', query: { job: existingId.value } })
}
</script>
